import Phaser from 'phaser';
import { Direction, TILE_SIZE } from '../data/world-config';
import type { GameScene } from '../scenes/GameScene';
import type { ActiveMonster } from './NightMonsterSystem';

// ---------------------------------------------------------------------------
// Input command definitions (Street Fighter-style directional inputs)
// ---------------------------------------------------------------------------
interface InputEntry {
  dir: string; // 'down','right','left','up' or 'none'
  time: number;
}

const COMBO_WINDOW = 800; // ms between hits to keep combo alive
const INPUT_BUFFER_SIZE = 10;
const INPUT_BUFFER_TIMEOUT = 600; // ms for command input freshness

export class CombatSystem {
  private scene: GameScene;

  // Player combat stats
  playerHp: number = 100;
  playerMaxHp: number = 100;
  lightDamage: number = 8;
  heavyDamage: number = 18;
  swordLevel: number = 1;
  playerCoins: number = 0;
  playerXp: number = 0;
  playerLevel: number = 1;
  xpToNextLevel: number = 50;

  // Energy (for specials)
  energy: number = 100;
  maxEnergy: number = 100;
  private energyRegen: number = 6; // per second (reduced from 10 — specials are more precious)

  // Attack state
  private isAttacking: boolean = false;
  private lightCooldown: number = 400; // ms (increased from 250 — sword is support, not primary)
  private heavyCooldown: number = 800; // ms (increased from 550)
  private lastAttackTime: number = 0;
  private swordSprite!: Phaser.GameObjects.Sprite;

  // Combo tracking
  comboCount: number = 0;
  private lastHitTime: number = 0;
  private comboTimer: number = 0;
  maxCombo: number = 0;

  // Input buffer for special move detection
  private inputBuffer: InputEntry[] = [];

  // Block / Dodge
  isBlocking: boolean = false;
  private isDodging: boolean = false;
  private dodgeCooldown: number = 0;
  private dodgeCooldownMax: number = 1500; // ms
  private invulnerable: boolean = false;

  // Projectiles
  private projectiles: { sprite: Phaser.GameObjects.Arc; vx: number; vy: number; damage: number; lifetime: number }[] = [];

  // Key tracking via DOM
  private keysDown: Set<string> = new Set();
  private onKeyDown!: (e: KeyboardEvent) => void;
  private onKeyUp!: (e: KeyboardEvent) => void;

  constructor(scene: GameScene) {
    this.scene = scene;
    this.createSwordSprite();
    this.setupInput();
    this.setupEventListeners();
  }

  private createSwordSprite(): void {
    this.swordSprite = this.scene.add.sprite(0, 0, 'weapons', 0);
    this.swordSprite.setDepth(12);
    this.swordSprite.setVisible(false);
    this.swordSprite.setOrigin(0.5, 1);
  }

  private setupInput(): void {
    this.onKeyDown = (e: KeyboardEvent) => {
      const code = e.code;
      if (code === 'KeyJ' || code === 'KeyK' || code === 'KeyL' ||
          code === 'ShiftLeft' || code === 'ShiftRight' || code === 'Space') {
        e.preventDefault();
        const wasDown = this.keysDown.has(code);
        this.keysDown.add(code);

        if (!wasDown) {
          if (code === 'KeyJ' || code === 'Space') this.tryLightAttack();
          else if (code === 'KeyK') this.tryHeavyAttack();
          else if (code === 'KeyL') this.trySpecialAttack();
          else if (code === 'ShiftLeft' || code === 'ShiftRight') this.startBlock();
        }
      }
    };
    this.onKeyUp = (e: KeyboardEvent) => {
      const code = e.code;
      if (code === 'KeyJ' || code === 'KeyK' || code === 'KeyL' ||
          code === 'ShiftLeft' || code === 'ShiftRight' || code === 'Space') {
        e.preventDefault();
        this.keysDown.delete(code);
        if (code === 'ShiftLeft' || code === 'ShiftRight') this.endBlock();
      }
    };
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
  }

  private setupEventListeners(): void {
    this.scene.events.on('player-damaged', (damage: number) => {
      this.takeDamage(damage);
    });

    this.scene.events.on('monster-killed', (data: { xp: number; coins: number; speciesId: string }) => {
      // Combo bonus: extra XP/coins based on combo
      const comboBonus = 1 + this.comboCount * 0.15;
      const xp = Math.floor(data.xp * comboBonus);
      const coins = Math.floor(data.coins * comboBonus);
      this.playerXp += xp;
      this.playerCoins += coins;

      // Show KO with combo info
      if (this.comboCount >= 3) {
        this.scene.events.emit('notification', `KO! ${this.comboCount}-hit combo! +${xp} XP, +${coins} coins`);
      } else {
        this.scene.events.emit('notification', `+${xp} XP, +${coins} coins`);
      }

      this.showKOEffect(data.speciesId);
      this.checkLevelUp();
      this.scene.events.emit('combat-stats-update', this.getStats());
    });

    this.scene.events.on('night-survived', () => {
      this.playerHp = Math.min(this.playerMaxHp, this.playerHp + 30);
      this.scene.events.emit('notification', 'Healed 30 HP at dawn!');
      this.scene.events.emit('combat-stats-update', this.getStats());
    });
  }

  // ===== PUBLIC API (touch controls) =====

  swingSwordPublic(): void {
    this.tryLightAttack();
  }

  heavyAttackPublic(): void {
    this.tryHeavyAttack();
  }

  specialAttackPublic(): void {
    this.trySpecialAttack();
  }

  startBlockPublic(): void {
    this.startBlock();
  }

  endBlockPublic(): void {
    this.endBlock();
  }

  /** Feed directional input from GameScene movement for special detection. */
  feedDirectionInput(dir: Direction): void {
    const dirNames = ['down', 'left', 'right', 'up'];
    this.inputBuffer.push({ dir: dirNames[dir], time: this.scene.time.now });
    if (this.inputBuffer.length > INPUT_BUFFER_SIZE) {
      this.inputBuffer.shift();
    }
  }

  // ===== LIGHT ATTACK (J / Space) =====

  private tryLightAttack(): void {
    const now = this.scene.time.now;
    if (now - this.lastAttackTime < this.lightCooldown) return;
    if (this.isAttacking || this.isBlocking) return;

    this.lastAttackTime = now;
    this.isAttacking = true;
    this.performLightAttack();
  }

  private performLightAttack(): void {
    const player = this.scene.player;
    const dir = player.direction;
    const off = this.getDirectionOffset(dir);
    const sx = player.sprite.x + off.x * 12;
    const sy = player.sprite.y + off.y * 12;

    // Quick sword jab animation
    this.swordSprite.setPosition(sx, sy);
    this.swordSprite.setAngle(off.angle);
    this.swordSprite.setVisible(true);
    this.swordSprite.setScale(1);

    const startAngle = off.angle - 30;
    const endAngle = off.angle + 30;
    this.swordSprite.setAngle(startAngle);

    this.scene.tweens.add({
      targets: this.swordSprite,
      angle: endAngle,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        this.swordSprite.setVisible(false);
        this.isAttacking = false;
      },
    });

    // Hit detection
    const damage = this.lightDamage + Math.floor(this.playerLevel * 1.2);
    this.checkHits(sx, sy, 20, damage, 80, 'light');
  }

  // ===== HEAVY ATTACK (K) =====

  private tryHeavyAttack(): void {
    const now = this.scene.time.now;
    if (now - this.lastAttackTime < this.heavyCooldown) return;
    if (this.isAttacking || this.isBlocking) return;

    this.lastAttackTime = now;
    this.isAttacking = true;
    this.performHeavyAttack();
  }

  private performHeavyAttack(): void {
    const player = this.scene.player;
    const dir = player.direction;
    const off = this.getDirectionOffset(dir);
    const sx = player.sprite.x + off.x * 14;
    const sy = player.sprite.y + off.y * 14;

    // Big sweeping swing
    this.swordSprite.setPosition(sx, sy);
    this.swordSprite.setAngle(off.angle);
    this.swordSprite.setVisible(true);
    this.swordSprite.setScale(1.4);

    const startAngle = off.angle - 60;
    const endAngle = off.angle + 60;
    this.swordSprite.setAngle(startAngle);

    // Camera shake for heavy feel
    this.scene.cameras.main.shake(80, 0.003);

    this.scene.tweens.add({
      targets: this.swordSprite,
      angle: endAngle,
      duration: 250,
      ease: 'Power3',
      onComplete: () => {
        this.swordSprite.setVisible(false);
        this.swordSprite.setScale(1);
        this.isAttacking = false;
      },
    });

    // Wider hit radius, more damage, more knockback
    const damage = this.heavyDamage + Math.floor(this.playerLevel * 2);
    this.checkHits(sx, sy, 28, damage, 160, 'heavy');
  }

  // ===== SPECIAL ATTACK (L) =====

  private trySpecialAttack(): void {
    if (this.isAttacking || this.isBlocking) return;

    // Check input buffer for directional commands
    const special = this.detectSpecialMove();

    if (special === 'hadouken' && this.energy >= 30) {
      this.energy -= 30;
      this.isAttacking = true;
      this.performHadouken();
    } else if (special === 'shoryuken' && this.energy >= 40) {
      this.energy -= 40;
      this.isAttacking = true;
      this.performShoryuken();
    } else if (this.energy >= 25) {
      // Default special: spinning slash (no directional input needed)
      this.energy -= 25;
      this.isAttacking = true;
      this.performSpinSlash();
    }

    this.scene.events.emit('combat-stats-update', this.getStats());
  }

  private detectSpecialMove(): string {
    const now = this.scene.time.now;
    // Get recent directional inputs (within buffer timeout)
    const recent = this.inputBuffer.filter(e => now - e.time < INPUT_BUFFER_TIMEOUT);
    if (recent.length < 2) return 'spin';

    const dirs = recent.map(e => e.dir);
    const last3 = dirs.slice(-3).join(',');
    const last2 = dirs.slice(-2).join(',');

    // Hadouken: down, down+forward, forward (simplified: down then forward)
    const facingDir = this.getFacingDirName();
    if (last3.includes(`down,${facingDir}`) || last2 === `down,${facingDir}`) {
      return 'hadouken';
    }

    // Shoryuken: forward, down, forward (simplified: forward then down)
    if (last3.includes(`${facingDir},down`) || last2 === `${facingDir},down`) {
      return 'shoryuken';
    }

    return 'spin';
  }

  // ===== HADOUKEN - Ranged projectile =====

  private performHadouken(): void {
    const player = this.scene.player;
    const dir = player.direction;
    const off = this.getDirectionOffset(dir);

    // Flash effect on player
    player.sprite.setTint(0x44aaff);
    this.scene.time.delayedCall(200, () => player.sprite.clearTint());

    this.scene.events.emit('notification', 'HADOUKEN!');

    // Camera flash
    this.scene.cameras.main.flash(150, 68, 170, 255, false);

    // Create projectile
    const startX = player.sprite.x + off.x * 16;
    const startY = player.sprite.y + off.y * 16;
    const speed = 180;
    const projectile = this.scene.add.circle(startX, startY, 5, 0x44aaff, 1);
    projectile.setDepth(13);

    // Glow ring around projectile
    const glow = this.scene.add.circle(startX, startY, 8, 0x44aaff, 0.3);
    glow.setDepth(12);

    const damage = 25 + Math.floor(this.playerLevel * 3);
    this.projectiles.push({
      sprite: projectile,
      vx: off.x * speed,
      vy: off.y * speed,
      damage,
      lifetime: 2000,
    });

    // Glow follows projectile
    this.scene.tweens.add({
      targets: glow,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 300,
      onComplete: () => glow.destroy(),
    });

    this.isAttacking = false;
  }

  // ===== SHORYUKEN - Dash forward strike =====

  private performShoryuken(): void {
    const player = this.scene.player;
    const dir = player.direction;
    const off = this.getDirectionOffset(dir);

    this.scene.events.emit('notification', 'SHORYUKEN!');

    // Become invulnerable during dash
    this.invulnerable = true;

    // Dash forward
    const startX = player.sprite.x;
    const startY = player.sprite.y;
    const dashDist = 50;

    player.sprite.setTint(0xffaa00);
    this.scene.cameras.main.shake(150, 0.008);

    this.scene.tweens.add({
      targets: player.sprite,
      x: startX + off.x * dashDist,
      y: startY + off.y * dashDist,
      duration: 200,
      ease: 'Power3',
      onUpdate: () => {
        // Hit anything along the dash path
        const damage = 35 + Math.floor(this.playerLevel * 4);
        this.checkHits(player.sprite.x, player.sprite.y, 24, damage, 200, 'special');
      },
      onComplete: () => {
        player.sprite.clearTint();
        this.invulnerable = false;
        this.isAttacking = false;
      },
    });
  }

  // ===== SPINNING SLASH - AOE around player =====

  private performSpinSlash(): void {
    const player = this.scene.player;

    this.scene.events.emit('notification', 'SPINNING SLASH!');

    // Show sword spinning 360 degrees
    this.swordSprite.setPosition(player.sprite.x, player.sprite.y);
    this.swordSprite.setVisible(true);
    this.swordSprite.setScale(1.6);

    player.sprite.setTint(0x44ff44);
    this.scene.cameras.main.shake(120, 0.006);

    // Create spin ring visual
    const ring = this.scene.add.circle(player.sprite.x, player.sprite.y, 30, 0x44ff44, 0.2);
    ring.setDepth(11);
    ring.setStrokeStyle(2, 0x44ff44, 0.8);

    this.scene.tweens.add({
      targets: ring,
      scaleX: 1.5,
      scaleY: 1.5,
      alpha: 0,
      duration: 400,
      onComplete: () => ring.destroy(),
    });

    this.scene.tweens.add({
      targets: this.swordSprite,
      angle: 360,
      duration: 350,
      ease: 'Power2',
      onComplete: () => {
        this.swordSprite.setVisible(false);
        this.swordSprite.setScale(1);
        this.swordSprite.setAngle(0);
        player.sprite.clearTint();
        this.isAttacking = false;
      },
    });

    // AOE damage around player
    const damage = 20 + Math.floor(this.playerLevel * 2.5);
    this.checkHits(player.sprite.x, player.sprite.y, 35, damage, 140, 'special');
  }

  // ===== BLOCKING =====

  private startBlock(): void {
    if (this.isAttacking) return;
    this.isBlocking = true;

    // Visual: blue tint + shield particles
    this.scene.player.sprite.setTint(0x6688cc);
    this.scene.events.emit('combat-stats-update', this.getStats());
  }

  private endBlock(): void {
    this.isBlocking = false;
    this.scene.player.sprite.clearTint();
    this.scene.events.emit('combat-stats-update', this.getStats());
  }

  // ===== DODGE ROLL (Shift + Direction while moving) =====

  tryDodge(vx: number, vy: number): boolean {
    if (this.dodgeCooldown > 0 || this.isDodging) return false;
    if (vx === 0 && vy === 0) return false;
    if (!this.isBlocking) return false; // Must be holding block to dodge

    this.isDodging = true;
    this.invulnerable = true;
    this.dodgeCooldown = this.dodgeCooldownMax;
    this.isBlocking = false;

    const player = this.scene.player;
    const dist = 60;
    const mag = Math.sqrt(vx * vx + vy * vy) || 1;
    const nx = vx / mag;
    const ny = vy / mag;

    // Ghost trail effect
    player.sprite.setAlpha(0.5);
    player.sprite.setTint(0xaaddff);

    // Create after-images
    for (let i = 0; i < 3; i++) {
      const ghost = this.scene.add.sprite(
        player.sprite.x + nx * i * 10,
        player.sprite.y + ny * i * 10,
        'player',
        player.sprite.frame.name as unknown as number
      );
      ghost.setDepth(9);
      ghost.setAlpha(0.4 - i * 0.1);
      ghost.setTint(0x6688cc);

      this.scene.tweens.add({
        targets: ghost,
        alpha: 0,
        duration: 300,
        delay: i * 50,
        onComplete: () => ghost.destroy(),
      });
    }

    this.scene.tweens.add({
      targets: player.sprite,
      x: player.sprite.x + nx * dist,
      y: player.sprite.y + ny * dist,
      duration: 200,
      ease: 'Power3',
      onComplete: () => {
        player.sprite.setAlpha(1);
        player.sprite.clearTint();
        this.isDodging = false;
        this.invulnerable = false;
      },
    });

    return true;
  }

  // ===== HIT DETECTION & COMBO =====

  private checkHits(sx: number, sy: number, radius: number, damage: number, knockback: number, type: string): void {
    if (!this.scene.nightMonsterSystem) return;

    const monsters = this.scene.nightMonsterSystem.getAliveMonsters();
    let hitCount = 0;

    for (const monster of monsters) {
      const dx = monster.sprite.x - sx;
      const dy = monster.sprite.y - sy;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist <= radius) {
        // Combo multiplier
        const now = this.scene.time.now;
        if (now - this.lastHitTime < COMBO_WINDOW) {
          this.comboCount++;
        } else {
          this.comboCount = 1;
        }
        this.lastHitTime = now;

        if (this.comboCount > this.maxCombo) {
          this.maxCombo = this.comboCount;
        }

        // Damage scales with combo (up to 1.5x at 10 combo)
        const comboMultiplier = 1 + Math.min(this.comboCount * 0.05, 0.5);
        const finalDamage = Math.floor(damage * comboMultiplier);

        this.scene.nightMonsterSystem.hitMonster(monster, finalDamage, knockback, type);
        this.createHitSpark(monster.sprite.x, monster.sprite.y, type);
        this.showDamageNumber(monster.sprite.x, monster.sprite.y, finalDamage, type);

        hitCount++;

        // Emit combo update
        this.scene.events.emit('combo-update', {
          count: this.comboCount,
          type,
        });
      }
    }

    // Whiff sound feel — slight forward lunge on miss
    if (hitCount === 0 && type !== 'special') {
      const player = this.scene.player;
      const off = this.getDirectionOffset(player.direction);
      this.scene.tweens.add({
        targets: player.sprite,
        x: player.sprite.x + off.x * 3,
        y: player.sprite.y + off.y * 3,
        duration: 80,
        yoyo: true,
        ease: 'Power2',
      });
    }
  }

  // ===== VISUAL EFFECTS =====

  private createHitSpark(x: number, y: number, type: string): void {
    const colors: Record<string, number> = {
      light: 0xffffff,
      heavy: 0xffaa00,
      special: 0x44aaff,
    };
    const color = colors[type] ?? 0xffffff;
    const count = type === 'special' ? 8 : type === 'heavy' ? 6 : 4;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
      const speed = 30 + Math.random() * 40;
      const spark = this.scene.add.circle(x, y, type === 'light' ? 1.5 : 2, color, 1);
      spark.setDepth(16);

      this.scene.tweens.add({
        targets: spark,
        x: x + Math.cos(angle) * speed,
        y: y + Math.sin(angle) * speed,
        alpha: 0,
        scaleX: 0.3,
        scaleY: 0.3,
        duration: 250 + Math.random() * 150,
        onComplete: () => spark.destroy(),
      });
    }

    // Flash circle at hit point
    const flash = this.scene.add.circle(x, y, type === 'special' ? 12 : 8, color, 0.6);
    flash.setDepth(15);
    this.scene.tweens.add({
      targets: flash,
      scaleX: 2,
      scaleY: 2,
      alpha: 0,
      duration: 200,
      onComplete: () => flash.destroy(),
    });
  }

  private showDamageNumber(x: number, y: number, damage: number, type: string): void {
    const colors: Record<string, string> = {
      light: '#ffffff',
      heavy: '#ffaa00',
      special: '#44aaff',
    };
    const color = colors[type] ?? '#ffffff';

    const prefix = this.comboCount >= 5 ? `${this.comboCount}x ` : '';
    const text = this.scene.add.text(x, y - 8, `${prefix}${damage}`, {
      fontFamily: 'monospace',
      fontSize: type === 'special' ? '10px' : '8px',
      color,
      stroke: '#000000',
      strokeThickness: 3,
      fontStyle: this.comboCount >= 3 ? 'bold' : 'normal',
    });
    text.setOrigin(0.5, 1);
    text.setDepth(20);

    this.scene.tweens.add({
      targets: text,
      y: y - 30,
      alpha: 0,
      duration: 800,
      ease: 'Power2',
      onComplete: () => text.destroy(),
    });
  }

  private showKOEffect(_speciesId: string): void {
    // Big "KO!" text at player position
    const player = this.scene.player;
    if (this.comboCount >= 2) {
      const koText = this.scene.add.text(player.sprite.x, player.sprite.y - 20, 'KO!', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ff4444',
        stroke: '#000000',
        strokeThickness: 4,
        fontStyle: 'bold',
      });
      koText.setOrigin(0.5, 0.5);
      koText.setDepth(25);

      this.scene.tweens.add({
        targets: koText,
        y: koText.y - 25,
        scaleX: 1.5,
        scaleY: 1.5,
        alpha: 0,
        duration: 1000,
        ease: 'Power2',
        onComplete: () => koText.destroy(),
      });
    }
  }

  // ===== DAMAGE & DEATH =====

  private takeDamage(amount: number): void {
    if (this.invulnerable) return;

    // Blocking reduces damage by 75%
    if (this.isBlocking) {
      amount = Math.floor(amount * 0.25);
      // Block flash effect
      this.scene.player.sprite.setTint(0x4488cc);
      this.scene.time.delayedCall(100, () => {
        if (this.isBlocking) {
          this.scene.player.sprite.setTint(0x6688cc);
        } else {
          this.scene.player.sprite.clearTint();
        }
      });
      this.scene.events.emit('notification', `Blocked! (-${amount})`);
    }

    this.playerHp = Math.max(0, this.playerHp - amount);

    // Reset combo on hit
    this.comboCount = 0;
    this.scene.events.emit('combo-update', { count: 0, type: 'reset' });

    this.scene.events.emit('combat-stats-update', this.getStats());

    if (this.playerHp <= 0) {
      this.onPlayerDeath();
    }
  }

  private onPlayerDeath(): void {
    this.scene.events.emit('notification', 'YOU WERE DEFEATED! Respawning...');
    this.comboCount = 0;

    this.scene.cameras.main.fade(1000, 0, 0, 0, false, (_camera: Phaser.Cameras.Scene2D.Camera, progress: number) => {
      if (progress >= 1) {
        this.playerHp = Math.floor(this.playerMaxHp / 2);
        this.energy = this.maxEnergy;
        const cx = 50 * TILE_SIZE;
        const cy = 50 * TILE_SIZE;
        this.scene.player.sprite.setPosition(cx, cy);
        this.scene.cameras.main.fadeIn(500);
        this.scene.events.emit('combat-stats-update', this.getStats());
      }
    });
  }

  // ===== LEVELING =====

  private checkLevelUp(): void {
    while (this.playerXp >= this.xpToNextLevel) {
      this.playerXp -= this.xpToNextLevel;
      this.playerLevel++;
      this.playerMaxHp += 10;
      this.playerHp = this.playerMaxHp;
      this.maxEnergy += 5;
      this.energy = this.maxEnergy;
      this.lightDamage += 2;
      this.heavyDamage += 4;
      this.xpToNextLevel = Math.floor(this.xpToNextLevel * 1.5);
      this.scene.events.emit('notification', `LEVEL UP! You are now level ${this.playerLevel}!`);

      // Level up flash
      this.scene.cameras.main.flash(300, 106, 170, 58, false);
    }
  }

  upgradeSword(): boolean {
    const cost = this.swordLevel * 25;
    if (this.playerCoins >= cost) {
      this.playerCoins -= cost;
      this.swordLevel++;
      this.lightDamage += 3;
      this.heavyDamage += 6;
      this.scene.events.emit('notification', `Sword upgraded to level ${this.swordLevel}!`);
      this.scene.events.emit('combat-stats-update', this.getStats());
      return true;
    }
    return false;
  }

  // ===== UPDATE (called from GameScene) =====

  update(time: number, delta: number): void {
    // Regen energy
    if (this.energy < this.maxEnergy) {
      this.energy = Math.min(this.maxEnergy, this.energy + this.energyRegen * (delta / 1000));
    }

    // Dodge cooldown
    if (this.dodgeCooldown > 0) {
      this.dodgeCooldown = Math.max(0, this.dodgeCooldown - delta);
    }

    // Combo timer decay
    if (this.comboCount > 0 && time - this.lastHitTime > COMBO_WINDOW) {
      this.comboCount = 0;
      this.scene.events.emit('combo-update', { count: 0, type: 'timeout' });
    }

    // Update projectiles
    this.updateProjectiles(delta);
  }

  private updateProjectiles(delta: number): void {
    const dt = delta / 1000;

    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.lifetime -= delta;

      if (p.lifetime <= 0) {
        p.sprite.destroy();
        this.projectiles.splice(i, 1);
        continue;
      }

      // Move projectile
      p.sprite.x += p.vx * dt;
      p.sprite.y += p.vy * dt;

      // Check hits against monsters
      if (this.scene.nightMonsterSystem) {
        const monsters = this.scene.nightMonsterSystem.getAliveMonsters();
        for (const monster of monsters) {
          const dx = monster.sprite.x - p.sprite.x;
          const dy = monster.sprite.y - p.sprite.y;
          if (Math.sqrt(dx * dx + dy * dy) < 16) {
            this.scene.nightMonsterSystem.hitMonster(monster, p.damage, 100, 'special');
            this.createHitSpark(monster.sprite.x, monster.sprite.y, 'special');
            this.showDamageNumber(monster.sprite.x, monster.sprite.y, p.damage, 'special');

            // Update combo
            const now = this.scene.time.now;
            if (now - this.lastHitTime < COMBO_WINDOW) {
              this.comboCount++;
            } else {
              this.comboCount = 1;
            }
            this.lastHitTime = now;
            this.scene.events.emit('combo-update', { count: this.comboCount, type: 'special' });

            // Explode projectile
            this.createHitSpark(p.sprite.x, p.sprite.y, 'special');
            p.sprite.destroy();
            this.projectiles.splice(i, 1);
            break;
          }
        }
      }
    }
  }

  // ===== HELPERS =====

  private getDirectionOffset(dir: Direction): { x: number; y: number; angle: number } {
    const offsets: Record<number, { x: number; y: number; angle: number }> = {
      [Direction.DOWN]: { x: 0, y: 1, angle: 0 },
      [Direction.UP]: { x: 0, y: -1, angle: 180 },
      [Direction.LEFT]: { x: -1, y: 0, angle: 90 },
      [Direction.RIGHT]: { x: 1, y: 0, angle: -90 },
    };
    return offsets[dir];
  }

  private getFacingDirName(): string {
    const dirNames = ['down', 'left', 'right', 'up'];
    return dirNames[this.scene.player.direction];
  }

  getStats() {
    return {
      hp: this.playerHp,
      maxHp: this.playerMaxHp,
      coins: this.playerCoins,
      xp: this.playerXp,
      xpToNext: this.xpToNextLevel,
      level: this.playerLevel,
      swordLevel: this.swordLevel,
      swordDamage: this.lightDamage + Math.floor(this.playerLevel * 1.2),
      energy: Math.floor(this.energy),
      maxEnergy: this.maxEnergy,
      comboCount: this.comboCount,
      isBlocking: this.isBlocking,
    };
  }

  shutdown(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    // Clean up projectiles
    for (const p of this.projectiles) {
      p.sprite.destroy();
    }
    this.projectiles = [];
  }
}
